/**
 * 课程播放页面工具函数
 */

/**
 * 获取URL参数
 * @param key 参数名
 * @returns 参数值
 */
export const getQueryString = (key: string): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(key) || '';
};

/**
 * 获取所有URL参数
 * @returns 参数对象
 */
export const getAllQueryParams = (): Record<string, string> => {
  const urlParams = new URLSearchParams(window.location.search);
  const params: Record<string, string> = {};
  for (const [key, value] of urlParams.entries()) {
    params[key] = value;
  }
  return params;
};

/**
 * 判断是否来自技能大师
 * @returns 是否来自技能大师
 */
export const isSkillMasterSource = (): boolean => {
  return getQueryString('source') === 'skillmaster';
};

/**
 * 格式化时间
 * @param seconds 秒数
 * @returns 格式化后的时间字符串
 */
export const formatTime = (seconds: number): string => {
  if (!seconds || seconds < 0) return '00:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @returns 格式化后的文件大小
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * 防抖函数
 * @param func 要防抖的函数
 * @param wait 等待时间
 * @returns 防抖后的函数
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * 节流函数
 * @param func 要节流的函数
 * @param wait 等待时间
 * @returns 节流后的函数
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  let previous = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (!previous) previous = now;
    const remaining = wait - (now - previous);

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      func(...args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        previous = Date.now();
        timeout = null;
        func(...args);
      }, remaining);
    }
  };
};

/**
 * 深拷贝
 * @param obj 要拷贝的对象
 * @returns 拷贝后的对象
 */
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
  if (obj instanceof Array) return obj.map((item) => deepClone(item)) as unknown as T;
  if (typeof obj === 'object') {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
  return obj;
};

/**
 * 生成唯一ID
 * @returns 唯一ID
 */
export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * 判断是否为移动端
 * @returns 是否为移动端
 */
export const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * 检查摄像头支持
 * @returns Promise<boolean>
 */
export const checkCameraSupport = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      resolve(false);
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(() => resolve(true))
      .catch(() => resolve(false));
  });
};

/**
 * 检查全屏支持
 * @returns 是否支持全屏
 */
export const checkFullscreenSupport = (): boolean => {
  return !!(
    document.fullscreenEnabled ||
    (document as any).webkitFullscreenEnabled ||
    (document as any).mozFullScreenEnabled ||
    (document as any).msFullscreenEnabled
  );
};

export const isCoursePlayPageMobileBrowser = () => {
  var sUserAgent = navigator.userAgent.toLowerCase();
  const isMainstreamPcSystem =
    sUserAgent.indexOf('mac os') > -1 ||
    sUserAgent.indexOf('windows nt') > -1 ||
    sUserAgent.indexOf('win32') > -1 ||
    sUserAgent.indexOf('win64') > -1;
  const isKylinSystem =
    (sUserAgent.indexOf('linux') > -1 || sUserAgent.indexOf('kylin') > -1) &&
    sUserAgent.indexOf('android') === -1;
  const isBigScreen =
    window.innerWidth >= 1024 ||
    document.documentElement.clientWidth >= 1024 ||
    document.body.clientWidth >= 1024;
  if ((isKylinSystem || isMainstreamPcSystem) && isBigScreen) return false;
  return true;
};

export const isWeixin = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('micromessenger');
};

// 判断是否在 app 环境中
export const isInAppEnvironment = (): boolean => {
  // 检查 iOS 注入的方法
  if (
    (window as any).webkit &&
    (window as any).webkit.messageHandlers &&
    (window as any).webkit.messageHandlers.Coaledu_Ios_Platform_Bridge_Event
  ) {
    return true;
  }
  // 检查 Android 注入的方法
  if ((window as any).Coaledu_Client_Android_Platform_Event) {
    return true;
  }
  return false;
};

export const formatSeconds = (value: string): string => {
  let result = parseInt(value) || 0;
  let h =
    Math.floor(result / 3600) < 10 ? '0' + Math.floor(result / 3600) : Math.floor(result / 3600);
  let m =
    Math.floor((result / 60) % 60) < 10
      ? '0' + Math.floor((result / 60) % 60)
      : Math.floor((result / 60) % 60);
  let s = Math.floor(result % 60) < 10 ? '0' + Math.floor(result % 60) : Math.floor(result % 60);
  let res = `${s}秒`;
  if (result >= 3600) {
    return `${h}时${m}分${s}秒`;
  } else if (result >= 60) {
    return `${m}分${s}秒`;
  }
  return res;
};
